/**
 * useMarketplaceListings Hook
 *
 * React Query hooks for fetching marketplace listings.
 */

import { useQuery, useInfiniteQuery } from '@tanstack/react-query';
import type { UseQueryResult, UseInfiniteQueryResult } from '@tanstack/react-query';
import { marketplaceService } from '../../../api/marketplace.service';
import type {
  MarketplaceListing,
  MarketplaceListingsResponse,
  MarketplaceSearchQuery,
  MarketplaceCategory,
} from '../../../types/marketplace.types';
import { getErrorMessage } from '../../../lib/utils/errors';

/**
 * Hook for fetching marketplace listings with pagination
 */
export const useMarketplaceListings = (
  query?: MarketplaceSearchQuery
): UseInfiniteQueryResult<MarketplaceListingsResponse, Error> => {
  return useInfiniteQuery<MarketplaceListingsResponse, Error>({
    queryKey: ['marketplace', 'listings', query],
    queryFn: async ({ pageParam }) => {
      console.log('[useMarketplaceListings] Fetching listings');
      return await marketplaceService.listListings({
        ...query,
        cursor: pageParam as string | undefined,
      });
    },
    getNextPageParam: (lastPage) => lastPage.hasMore ? lastPage.cursor : undefined,
    initialPageParam: undefined,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Hook for fetching a single marketplace listing
 */
export const useMarketplaceListing = (
  listingId: string
): UseQueryResult<MarketplaceListing, Error> => {
  return useQuery<MarketplaceListing, Error>({
    queryKey: ['marketplace', 'listing', listingId],
    queryFn: async () => {
      console.log('[useMarketplaceListing] Fetching listing:', listingId);
      return await marketplaceService.getListing(listingId);
    },
    enabled: !!listingId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Hook for fetching listings by category
 */
export const useMarketplaceListingsByCategory = (
  category: MarketplaceCategory,
  query?: MarketplaceSearchQuery
): UseInfiniteQueryResult<MarketplaceListingsResponse, Error> => {
  return useInfiniteQuery<MarketplaceListingsResponse, Error>({
    queryKey: ['marketplace', 'category', category, query],
    queryFn: async ({ pageParam }) => {
      console.log('[useMarketplaceListingsByCategory] Fetching category:', category);
      return await marketplaceService.getListingsByCategory(category, {
        ...query,
        cursor: pageParam as string | undefined,
      });
    },
    getNextPageParam: (lastPage) => lastPage.hasMore ? lastPage.cursor : undefined,
    initialPageParam: undefined,
    enabled: !!category,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Hook for fetching marketplace categories
 */
export const useMarketplaceCategories = (): UseQueryResult<MarketplaceCategory[], Error> => {
  return useQuery<MarketplaceCategory[], Error>({
    queryKey: ['marketplace', 'categories'],
    queryFn: async () => {
      console.log('[useMarketplaceCategories] Fetching categories');
      return await marketplaceService.getCategories();
    },
    staleTime: 30 * 60 * 1000, // 30 minutes (categories don't change often)
  });
};
